/**
 * Sprint 23.8 — WorkforcePulseCard (hero section 3: "today").
 *
 * The compact single-card alternative to the Workforce route's separate cards:
 * up to 5 most-active agents in one elevated card, each a row of avatar + name +
 * role + live status (dot + verb + duration). Roster from `useListTree`, status
 * from `useWorkforceStatus` (polled 30s, shared with the Workforce route via the
 * query key). Caps at 5 (most-active first); the Workforce route owns the full
 * list, so there's no "see all" here.
 */

import { useMemo, type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { Avatar } from "../primitives";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useWorkforceStatus } from "../../hooks/use-workforce-status";
import { useListTree } from "../../../lib/hooks/use-kuvio";
import { useAuth } from "../../../lib/hooks/use-auth";
import type { AgentStatus, LibraryNode } from "../../../lib/types";
import { formatAge } from "./format";

const STATUS_RANK: Record<string, number> = { working: 0, awaiting: 1, available: 2 };

export function WorkforcePulseCard() {
  const { currentWorkspaceId } = useAuth();
  const labels = useThemedLabels();
  const navigate = useNavigate();
  const tree = useListTree(currentWorkspaceId ?? undefined);

  const agents = useMemo<LibraryNode[]>(
    () => (tree.data ?? []).filter((n) => n.type === "agent" && !n.archived_at),
    [tree.data],
  );
  const agentIds = useMemo(() => agents.map((a) => a.id), [agents]);
  const statusQuery = useWorkforceStatus(agentIds, agents.length > 0);
  const statusMap = statusQuery.data ?? {};

  // Most-active first: working → awaiting → available, then the freshest
  // available, then name. Capped at 5 (the spec's pulse window).
  const top = useMemo(() => {
    const copy = [...agents];
    copy.sort((a, b) => {
      const sa = statusMap[a.id]?.status ?? "available";
      const sb = statusMap[b.id]?.status ?? "available";
      const rank = (STATUS_RANK[sa] ?? 2) - (STATUS_RANK[sb] ?? 2);
      if (rank !== 0) return rank;
      if (sa === "available") {
        const da = statusMap[a.id]?.duration_seconds ?? Number.MAX_SAFE_INTEGER;
        const db = statusMap[b.id]?.duration_seconds ?? Number.MAX_SAFE_INTEGER;
        if (da !== db) return da - db;
      }
      return a.name.localeCompare(b.name);
    });
    return copy.slice(0, 5);
  }, [agents, statusMap]);

  if (agents.length === 0) return null; // page redirects on 0 agents; defensive.

  return (
    <section>
      <h3 style={sectionHeader}>{labels.workspaceHomePulseTitle}</h3>
      <div style={pulseCard}>
        {top.map((agent) => (
          <div
            key={agent.id}
            style={pulseRow}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/agents/${agent.id}`)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(`/agents/${agent.id}`);
              }
            }}
          >
            <Avatar
              persona={getAgentPersona({ path: agent.path, agent_config: agent.agent_config as Record<string, unknown> | null })}
              name={agent.name}
              size="md"
              title={agent.name}
            />
            <div style={nameCol}>
              <span style={nameText}>{agent.name}</span>
              {agent.description?.trim() && <span style={roleText}>{agent.description}</span>}
            </div>
            <StatusText status={statusMap[agent.id] ?? null} labels={labels} />
          </div>
        ))}
      </div>
    </section>
  );
}

function StatusText({
  status,
  labels,
}: {
  status: AgentStatus | null;
  labels: ReturnType<typeof useThemedLabels>;
}) {
  const kind = status?.status ?? "available";
  const dur = formatAge(status?.duration_seconds ?? null);
  let dotColor: string;
  let textColor: string;
  let weight: number;
  let text: string;
  if (kind === "working") {
    dotColor = "var(--color-text-success)";
    textColor = "var(--color-text-success)";
    weight = 500;
    text = `${status?.verb || labels.statusWorking}${dur ? ` · ${dur}` : ""}`;
  } else if (kind === "awaiting") {
    dotColor = "var(--color-text-warning)";
    textColor = "var(--color-text-warning)";
    weight = 500;
    text = `${labels.statusAwaiting}${dur ? ` · ${dur}` : ""}`;
  } else {
    dotColor = "var(--color-text-tertiary)";
    textColor = "var(--color-text-secondary)";
    weight = 400;
    text = `${labels.statusAvailable}${dur ? ` · last ${dur}` : ""}`;
  }
  return (
    <span style={statusWrap}>
      <span style={{ ...statusDot, background: dotColor }} />
      <span style={{ ...statusLabel, color: textColor, fontWeight: weight }}>{text}</span>
    </span>
  );
}

const sectionHeader: CSSProperties = {
  margin: "0 0 8px",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const pulseCard: CSSProperties = {
  background: "var(--color-background-secondary)",
  borderRadius: "var(--border-radius-md)",
  padding: "10px 12px",
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const pulseRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
};

const nameCol: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "baseline",
  gap: 8,
};

const nameText: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  flexShrink: 0,
};

const roleText: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const statusWrap: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  flexShrink: 0,
};

const statusDot: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: "50%",
  flexShrink: 0,
};

const statusLabel: CSSProperties = {
  fontSize: 11,
};
